'use client';

import type { SortDirection } from '@morpho-org/ui-v6/client';
import { InterfaceChainUtils } from '@morpho-org/dapp-core';
import { isDefined } from '@morpho-org/morpho-ts';
import { TableNoFilterResults } from '@morpho-org/ui-v6/client';
import { CompactTable, computeActiveCount, type OptionTableProperties, SortBy, TablePropertiesPopover } from '@morpho-org/ui-v6/client';
import { useIsBelowBreakpoint } from '@morpho-org/ui-v6/hooks/useMediaQuery';
import { cn } from '@morpho-org/ui-v6/utils';
import { Button, Tooltip, TooltipContent, TooltipTrigger } from '@repo/ui';
import { BREAKPOINTS_PX } from '@repo/ui/breakpoints';
import { Collapse, Expand } from '@repo/ui/icons';
import { keepPreviousData, useInfiniteQuery } from '@tanstack/react-query';
import { flexRender, useTable } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { TableQueryError } from '@/components/common/Error';
import { InternalLink } from '@/components/common/InternalLink';
import { InWalletFilter } from '@/components/common/InWalletFilter';
import { TableNetworkFilter } from '@/components/common/TableAdvancedFilters';
import AssetFilter from '@/components/common/TableAdvancedFilters/AssetFilter';
import CuratorFilter from '@/components/common/TableAdvancedFilters/CuratorFilter';
import { ChainConfig } from '@/config';
import { useWeb3Context } from '@/contexts/Web3Context';
import useTableSettings, { type TableSettingsProps } from '@/helpers/cookieStorage/userSettings/tableSettings/client';
import { PageKey } from '@/helpers/cookieStorage/userSettings/types';
import { assetsToFilterItems, collateralAssetsToFilterItems, collateralFacetsToAssetItems, getFacetFilters } from '@/helpers/filter/vaultMasterFilter/getFacetFilters';
import { getInfiniteSkeletonRowCount } from '@/helpers/getTableSkeletonRowCount';
import { getVaultUrl } from '@/helpers/routing';
import { useInfiniteTableScroll } from '@/hooks/useInfiniteTableScroll';
import { useInWalletFilter } from '@/hooks/useInWalletFilter';
import { useMasterFilterTracking } from '@/lib/analytics/client/hooks/useMasterFilterTracking';
import { trackAnalyticsEvent } from '@/lib/analytics/client/track';
import { trackColumnToggled } from '@/lib/analytics/client/trackers/column';
import { trackFilterChanged } from '@/lib/analytics/client/trackers/filter';
import { trackSortChanged } from '@/lib/analytics/client/trackers/sort';
import MobileVaultCardList from './components/MobileVaultCardList';
import { VaultsMasterFilter } from './components/VaultsMasterFilter';
import VaultTableFilters from './components/VaultTableFilters';
import { clearVaultsFilters, COLUMN_IDS, EXPLORE_VAULTS_COLUMN_LABELS, type ExploreVaultsFilters, hasActiveVaultsFilters, resetVaultsFiltersToInitial, ROW_HEIGHT_ESTIMATE, SORT_BY_OPTIONS, VAULTS_DEFAULT_TABLE_SETTINGS, VAULTS_INITIAL_SORTING, VAULTS_PAGE_SIZE, VAULTS_STORAGE_KEY, VIRTUALIZER_INITIAL_RECT, VIRTUALIZER_OVERSCAN } from './config/config';
import { tableVaultFeatures, vaultColumnsDefs } from './config/TableVault.columns';
import { isVaultListInputWithinBounds, toVaultListInput, vaultListInfiniteOptions } from './config/vaultListQuery';
import { useTableVaultRow, useUserVaultPositions, useVaultsMasterFilter, useVaultsTableSorting } from './hooks';
import { DEFAULT_MASTER_FILTER_STATE, VAULTS_ANALYTICS_EXCLUDE_KEYS } from './hooks/useVaultsMasterFilter';
export interface TableVaultClientProps {
  initialTableSettings: TableSettingsProps['initialTableSettings'];
  /** Request-derived phone guess (`getIsMobileRequest`), used until `matchMedia` takes over after hydration. */
  initialIsMobile: boolean;
}
const FILTER_TOO_LONG_MESSAGE = 'Too many filters selected. Remove some to load vaults.';
const TableVaultClient = ({
  initialTableSettings,
  initialIsMobile
}: TableVaultClientProps) => {
  const isMobile = useIsBelowBreakpoint(BREAKPOINTS_PX.md, {
    serverSnapshot: initialIsMobile
  });
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [isFullscreenHovered, setIsFullscreenHovered] = useState(false);
  const {
    account
  } = useWeb3Context();
  const {
    urlParams: filters,
    setUrlParams: setFilters,
    masterFilterState,
    onMasterFilterChange,
    isTransitionPending,
    startTransition
  } = useVaultsMasterFilter();
  const masterFilterTracking = useMasterFilterTracking('vaults');
  const {
    sorting,
    setSorting,
    columnVisibility,
    setColumnVisibility,
    setColumnOrder,
    columnOrder
  } = useTableSettings({
    pageKey: PageKey.Earn,
    tableSettingsObjectKey: VAULTS_STORAGE_KEY,
    defaultTableSettings: VAULTS_DEFAULT_TABLE_SETTINGS,
    initialTableSettings
  });
  const {
    orderBy,
    desc
  } = useVaultsTableSorting(sorting);
  const sortingItem = sorting[0];
  const sortByDirection: SortDirection = sortingItem?.desc ? 'desc' : 'asc';
  const isDefaultSorting = sortingItem?.id === VAULTS_INITIAL_SORTING[0]!.id && sortingItem?.desc === VAULTS_INITIAL_SORTING[0]!.desc;
  const handleSortByOptionChange = (value: string) => {
    const nextDesc = sortingItem?.desc ?? true;
    setSorting([{
      id: value,
      desc: nextDesc
    }]);
    if (columnVisibility[value] === false) {
      setColumnVisibility(prev => ({
        ...prev,
        [value]: true
      }));
    }
    trackSortChanged({
      page: 'vaults',
      sortBy: value,
      sortDirection: nextDesc ? 'desc' : 'asc',
      trigger: 'option',
      isDefault: value === VAULTS_INITIAL_SORTING[0]!.id && nextDesc === VAULTS_INITIAL_SORTING[0]!.desc
    });
  };
  const handleSortByDirectionChange = (direction: SortDirection) => {
    const nextSortBy = sortingItem?.id ?? VAULTS_INITIAL_SORTING[0]!.id;
    setSorting([{
      id: nextSortBy,
      desc: direction === 'desc'
    }]);
    trackSortChanged({
      page: 'vaults',
      sortBy: nextSortBy,
      sortDirection: direction,
      trigger: 'direction',
      isDefault: nextSortBy === VAULTS_INITIAL_SORTING[0]!.id && direction === 'desc' === VAULTS_INITIAL_SORTING[0]!.desc
    });
  };
  const handleSortByClear = () => {
    setSorting(VAULTS_INITIAL_SORTING);
    const defaultSort = VAULTS_INITIAL_SORTING[0]!;
    trackSortChanged({
      page: 'vaults',
      sortBy: defaultSort.id,
      sortDirection: defaultSort.desc ? 'desc' : 'asc',
      trigger: 'clear',
      isDefault: true
    });
  };
  const listInput = toVaultListInput(filters, {
    orderBy,
    desc
  });
  const isFilterTooLong = !isVaultListInputWithinBounds(listInput);
  const listOptions = vaultListInfiniteOptions(listInput);
  const infiniteQueryKey = listOptions.queryKey;
  const {
    data: infiniteData,
    isLoading: isQueryLoading,
    isFetching,
    isFetchingNextPage,
    hasNextPage,
    fetchNextPage,
    error
  } = useInfiniteQuery({
    ...listOptions,
    enabled: !isFilterTooLong,
    placeholderData: keepPreviousData
  });
  const allVaults = useMemo(() => infiniteData?.pages.flatMap(page => page.items) ?? [], [infiniteData]);
  const firstPage = infiniteData?.pages[0];
  const facets = firstPage?.facets ?? undefined;
  const isPartial = infiniteData?.pages.some(page => page.partial) ?? false;
  const hasExclusions = filters.collateralExclusion.length > 0;
  const {
    filteredAssets,
    filteredCurators,
    filteredChainCounts,
    filteredChainTvls,
    filteredCollateralAssets,
    filteredCollateralExclusionAssets
  } = useMemo(() => getFacetFilters({
    facets
  }), [facets]);
  const {
    isLoading: isWalletFilterLoading,
    isActive: isWalletFilterActive,
    isError: isWalletFilterError,
    toggle: toggleWalletFilter,
    reset: resetInWalletFilter,
    holdingsSelected
  } = useInWalletFilter({
    assets: filteredAssets,
    currentAssets: filters.assets,
    onChange: assetSelectors => {
      onFiltersChange({
        ...filters,
        assets: assetSelectors
      });
    }
  });
  const inWalletFilterEmptyState = isWalletFilterActive && !isWalletFilterLoading && !isWalletFilterError && holdingsSelected.length === 0;
  const showChainOptions = {
    showChain: true
  } as const;
  const collateralExposureAssets = filteredCollateralAssets.length ? collateralFacetsToAssetItems(filteredCollateralAssets) : filteredAssets;
  const collateralExposureItems = filteredCollateralAssets.length ? collateralAssetsToFilterItems(filteredCollateralAssets, masterFilterState.collateralExposure, showChainOptions) : assetsToFilterItems(filteredAssets, masterFilterState.collateralExposure, showChainOptions);
  const exclusionCollateralFacets = hasExclusions ? filteredCollateralExclusionAssets : filteredCollateralAssets;
  const collateralExclusionAssets = exclusionCollateralFacets.length ? collateralFacetsToAssetItems(exclusionCollateralFacets) : filteredAssets;
  const collateralExclusionItems = exclusionCollateralFacets.length ? collateralAssetsToFilterItems(exclusionCollateralFacets, masterFilterState.collateralExclusion, showChainOptions) : assetsToFilterItems(filteredAssets, masterFilterState.collateralExclusion, showChainOptions);
  const userVaultPositionKeys = useUserVaultPositions();
  const vaultRows = useTableVaultRow({
    vaults: allVaults,
    userVaultPositionKeys
  });
  const table = useTable({
    features: tableVaultFeatures,
    data: vaultRows,
    columns: vaultColumnsDefs,
    state: {
      sorting,
      columnVisibility,
      columnOrder
    },
    manualSorting: true,
    enableSortingRemoval: false,
    onSortingChange: setSorting,
    onColumnVisibilityChange: setColumnVisibility,
    onColumnOrderChange: setColumnOrder
  });
  const onFiltersChange = (newFilters: ExploreVaultsFilters) => {
    startTransition(() => {
      setFilters(newFilters);
    });
  };
  const handleSearchChange = (value: string) => {
    onFiltersChange({
      ...filters,
      search: value.trim()
    });
  };
  const isFilterFetching = isFetching && !isQueryLoading && !isFetchingNextPage;
  const isLoadingFullTable = isFilterFetching || isWalletFilterLoading || isTransitionPending;
  const {
    sentinelRef,
    scrollContainerRef
  } = useInfiniteTableScroll({
    queryKey: infiniteQueryKey,
    hasNextPage,
    isFetchingNextPage,
    isLoadingFullTable,
    fetchNextPage,
    enabled: !inWalletFilterEmptyState,
    isMobile
  });

  // Track what the table shows. Zero when the wallet filter resolves to no holdings, since the
  // API then sees no asset restriction and keeps returning the global total. Blank while a
  // filter change is in flight, since keepPreviousData holds the previous filter's count, and
  // blank rather than undefined so AdvancedFilter does not fall back to its "N selected".
  const shownResultCount = inWalletFilterEmptyState ? 0 : isLoadingFullTable ? undefined : firstPage?.countTotal;
  const filterResultLabel = shownResultCount == null ? '' : `${shownResultCount.toLocaleString('en-US')} ${shownResultCount === 1 ? 'vault' : 'vaults'}`;
  const skeletonRowCount = getInfiniteSkeletonRowCount(VAULTS_PAGE_SIZE, {
    isLoadingFullTable: isQueryLoading || isLoadingFullTable,
    isFetchingNextPage,
    totalCount: firstPage?.countTotal,
    loadedCount: allVaults.length
  });
  const skeletonRows = useMemo(() => Array.from({
    length: skeletonRowCount
  }, () => vaultColumnsDefs.filter(def => {
    if (!isDefined(columnVisibility) || !isDefined(def?.id)) return true;
    if (!isDefined(columnVisibility[def.id])) return true;
    return columnVisibility[def.id];
  }).map(def => def.skeletonCell)), [skeletonRowCount, columnVisibility]);
  const dataRows = table.getRowModel().rows;
  const rowVirtualizer = useVirtualizer({
    count: isLoadingFullTable ? 0 : dataRows.length,
    getScrollElement: () => scrollContainerRef.current,
    estimateSize: () => ROW_HEIGHT_ESTIMATE,
    overscan: VIRTUALIZER_OVERSCAN,
    initialRect: VIRTUALIZER_INITIAL_RECT
  });
  const virtualRows = rowVirtualizer.getVirtualItems();
  const paddingTop = virtualRows[0]?.start ?? 0;
  const paddingBottom = virtualRows.length > 0 ? rowVirtualizer.getTotalSize() - (virtualRows.at(-1)?.end ?? 0) : 0;
  const columns = columnOrder.length > 0 ? columnOrder : table.getAllLeafColumns().map(column => column.id);
  const columnOptions = columns.map(column => {
    const label = EXPLORE_VAULTS_COLUMN_LABELS[column];
    if (!isDefined(label)) {
      return null;
    }
    const isActive = isDefined(columnVisibility[column]) ? !!columnVisibility[column] : true;
    return {
      label,
      value: column,
      isActive,
      isDisabled: column === COLUMN_IDS.Vault,
      action: () => {
        table.getColumn(column)?.toggleVisibility();
        trackColumnToggled({
          page: 'vaults',
          column,
          isVisible: !isActive
        });
      }
    };
  }).filter(isDefined);
  const handleColumnReorder = (orderedOptions: OptionTableProperties<string>[]) => {
    table.setColumnOrder(orderedOptions.map(option => option.value));
  };
  const isInitialLoading = isQueryLoading && !isFilterTooLong || isWalletFilterLoading || isTransitionPending || isFilterFetching;
  const hasError = isFilterTooLong || error || isWalletFilterError && isWalletFilterActive;
  const hasRows = !inWalletFilterEmptyState && table.getRowModel().rows.length > 0;
  const showTable = (hasRows || isInitialLoading || isFetchingNextPage) && !hasError;
  const showEmptyState = !hasRows && !hasError && !isInitialLoading;
  const hasFilters = hasActiveVaultsFilters(filters, {
    isWalletFilterActive
  });

  // Every user-driven change to the version filter emits this, whichever control did it.
  const trackV1Change = (nextV1: boolean) => {
    if (nextV1 === filters.v1) return;
    trackFilterChanged({
      filterName: 'vault_v1',
      filterValue: nextV1,
      page: 'vaults'
    });
  };
  const clearFilters = () => {
    resetInWalletFilter();
    const next = clearVaultsFilters(filters);
    trackV1Change(next.v1);
    onFiltersChange(next);
  };

  // A wallet change drops wallet-tied filters; it is not a request to widen the list,
  // so it restores the arrival state rather than the "show all" baseline. The version
  // choice is the user's and survives.
  const resetFiltersForWalletChange = () => {
    resetInWalletFilter();
    onFiltersChange(resetVaultsFiltersToInitial(filters));
  };

  // Clear filters when wallet address changes
  const prevAddressRef = useRef(account?.address);
  useEffect(() => {
    if (prevAddressRef.current !== undefined && prevAddressRef.current !== account?.address) {
      resetFiltersForWalletChange();
    }
    prevAddressRef.current = account?.address;
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Only trigger on wallet change
  }, [account?.address]);
  const isFullscreenRef = useRef(isFullscreen);
  isFullscreenRef.current = isFullscreen;
  const toggleFullscreen = useCallback(() => {
    const next = !isFullscreenRef.current;
    trackAnalyticsEvent('table_fullscreen_toggled', {
      page: 'vaults',
      is_fullscreen: next,
      trigger: 'button'
    });
    setIsFullscreen(next);
  }, []);
  useEffect(() => {
    if (!isFullscreen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        trackAnalyticsEvent('table_fullscreen_toggled', {
          page: 'vaults',
          is_fullscreen: false,
          trigger: 'keyboard'
        });
        setIsFullscreen(false);
      }
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isFullscreen]);
  return <div data-testid="listing-table" className="gap-sm flex min-h-0 w-full flex-1 flex-col max-md:gap-0">
      <div className="gap-sm flex min-h-0 flex-1 flex-col max-md:gap-0">
        <div className={cn('shadow-md dark:shadow-none border-[0.5px] border-border max-md:bg-background bg-card flex min-h-0 flex-1 flex-col rounded-[20px] max-md:rounded-none max-md:border-none max-md:shadow-none', isFullscreen && 'z-50 fixed top-[var(--navbar-height,_70px)] right-[var(--frame-border-size,_10px)] bottom-[var(--frame-border-size,_10px)] left-[var(--frame-border-size,_10px)]')}>
          <div className="max-md:bg-background bg-card sticky z-1 overflow-hidden rounded-t-[20px] max-md:top-[var(--navbar-height)]">
            <div data-testid="listing-filters" className="p-md box-border content-center rounded-t-[20px] max-md:rounded-none max-md:border-none max-md:bg-transparent max-md:p-[16px]">
              <VaultTableFilters networkFilterSlot={<TableNetworkFilter chainIdsFilter={masterFilterState.chains} handleChainIdsFilterChange={chains => onMasterFilterChange({
              ...masterFilterState,
              chains
            })} disabled={isWalletFilterLoading} chainCounts={filteredChainCounts} chainTvls={filteredChainTvls} hasHighestZIndex={isFullscreen} resultLabel={filterResultLabel} page="vaults" />} assetFilterSlot={<AssetFilter label="asset" allLabel="Asset" allAssets={filteredAssets} assets={masterFilterState.assets} chainIds={masterFilterState.chains.length > 0 ? masterFilterState.chains : ChainConfig.AVAILABLE_CHAIN_IDS} onAssetsChange={assets => {
              const next = {
                ...masterFilterState,
                assets
              };
              onMasterFilterChange(next);
              masterFilterTracking.onFieldChange('assets', computeActiveCount(next, DEFAULT_MASTER_FILTER_STATE, VAULTS_ANALYTICS_EXCLUDE_KEYS));
            }} disabled={isWalletFilterLoading} hasHighestZIndex={isFullscreen} resultLabel={filterResultLabel} showPTFilter />} curatorFilterSlot={<CuratorFilter curators={filteredCurators} curatorIds={masterFilterState.curators} onCuratorIdsChange={curators => {
              const next = {
                ...masterFilterState,
                curators
              };
              onMasterFilterChange(next);
              masterFilterTracking.onFieldChange('curators', computeActiveCount(next, DEFAULT_MASTER_FILTER_STATE, VAULTS_ANALYTICS_EXCLUDE_KEYS));
            }} disabled={isWalletFilterLoading} allLabel="Curator" hasHighestZIndex={isFullscreen} resultLabel={filterResultLabel} />} masterFilterSlot={<VaultsMasterFilter value={masterFilterState} onChange={newState => {
              trackV1Change(newState.v1);
              onMasterFilterChange(newState);
            }} allCollateralExposureAssets={collateralExposureAssets} allCollateralExclusionAssets={collateralExclusionAssets} collateralExposureItems={collateralExposureItems} collateralExclusionItems={collateralExclusionItems} disabled={isWalletFilterLoading} />} holdingsFilterSlot={account.isConnected ? <InWalletFilter isActive={isWalletFilterActive} onChange={toggleWalletFilter} tooltipContent="Filter vaults by wallet holdings." page="vaults" /> : undefined} editPropertiesSlot={!isMobile ? <TablePropertiesPopover options={columnOptions} setColumnOrder={handleColumnReorder} /> : undefined} fullscreenSlot={!isMobile ? <Tooltip>
                      <TooltipTrigger asChild>
                        <Button variant="ghost" size="iconSm" aria-label={isFullscreen ? 'Exit full screen' : 'Browse in full screen'} onClick={toggleFullscreen} onMouseEnter={() => setIsFullscreenHovered(true)} onMouseLeave={() => setIsFullscreenHovered(false)}>
                          {isFullscreen ? <Collapse isExpanded={!isFullscreenHovered} /> : <Expand isExpanded={isFullscreenHovered} />}
                        </Button>
                      </TooltipTrigger>
                      <TooltipContent>
                        {isFullscreen ? 'Exit full screen' : 'Browse in full screen'}
                      </TooltipContent>
                    </Tooltip> : undefined} sortBySlot={<SortBy options={SORT_BY_OPTIONS} selectedOption={sortingItem?.id ?? null} direction={sortByDirection} onOptionChange={handleSortByOptionChange} onDirectionChange={handleSortByDirectionChange} onClear={handleSortByClear} clearable={!isDefaultSorting} />} searchValue={filters.search} onSearchChange={handleSearchChange} disabled={isWalletFilterLoading} />
            </div>
          </div>

          {isPartial && !hasError && <p data-testid="vault-table-partial-notice" className="text-secondary-foreground px-md pb-sm text-sm max-md:px-[16px]">
              Some vaults could not be loaded, so this list may be incomplete.
            </p>}

          {isMobile ? <div className="mx-[10px]">
              {showTable && <MobileVaultCardList rows={isLoadingFullTable ? [] : table.getRowModel().rows} isLoading={isInitialLoading} isFetchingMore={isFetchingNextPage} skeletonCount={skeletonRowCount} />}

              {showEmptyState && <TableNoFilterResults type="vaults" hasFilters={hasFilters} clearFilters={clearFilters} minHeight="250px" />}

              {hasError && (isFilterTooLong ? <TableNoFilterResults type="vaults" hasFilters clearFilters={clearFilters} minHeight="250px" message={FILTER_TOO_LONG_MESSAGE} /> : <TableQueryError error={error} minHeight="250px" style={{
            borderRadius: '0',
            backgroundColor: 'transparent'
          }} />)}

              {hasNextPage && !hasError && !inWalletFilterEmptyState && <div ref={sentinelRef} className="h-px" />}
            </div> : <div className="border-border grid min-h-0 flex-1 grid-cols-[minmax(0,1fr)] overflow-hidden rounded-b-[20px] border-t-[0.5px]">
              <div ref={scrollContainerRef} className="max-h-full min-h-0 overflow-auto overscroll-y-contain" data-testid="table-scroll-container">
                {showTable && <CompactTable className="table-fixed">
                    <CompactTable.Head data-testid="listing-table-header" headerGroups={table.getHeaderGroups()} columnLabels={EXPLORE_VAULTS_COLUMN_LABELS} onColumnOrderChange={table.setColumnOrder} columnOrder={columns} />
                    <CompactTable.Body>
                      {!isLoadingFullTable && <>
                          {paddingTop > 0 && <tr>
                              <td colSpan={table.getVisibleLeafColumns().length} style={{
                      height: paddingTop
                    }} />
                            </tr>}
                          {virtualRows.map(virtualRow => {
                    const row = dataRows[virtualRow.index];
                    if (!row) return null;
                    const href = getVaultUrl({
                      identifier: InterfaceChainUtils.getIdentifierById(row.original.vault.chainId),
                      vaultAddress: row.original.vault.address,
                      vaultName: row.original.vault.name
                    });
                    return <CompactTable.Row key={row.id}>
                                {row.getVisibleCells().map(cell => <CompactTable.Cell key={cell.id}>
                                    <InternalLink href={href} prefetch="on-hover-debounced">
                                      <CompactTable.CellContent>
                                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                                      </CompactTable.CellContent>
                                    </InternalLink>
                                  </CompactTable.Cell>)}
                              </CompactTable.Row>;
                  })}
                          {paddingBottom > 0 && <tr>
                              <td colSpan={table.getVisibleLeafColumns().length} style={{
                      height: paddingBottom
                    }} />
                            </tr>}
                        </>}

                      {skeletonRows.map((row, index) => <CompactTable.SkeletonRow key={`${index}-skeleton-vault`} cells={row} keyPrefix={`${index}-skeleton-vault`} />)}
                    </CompactTable.Body>
                  </CompactTable>}

                {showEmptyState && <TableNoFilterResults type="vaults" withBackground hasFilters={hasFilters} clearFilters={clearFilters} height="100%" />}

                {hasError && (isFilterTooLong ? <TableNoFilterResults type="vaults" withBackground hasFilters clearFilters={clearFilters} height="100%" message={FILTER_TOO_LONG_MESSAGE} /> : <TableQueryError error={error} minHeight="688px" style={{
              borderRadius: '0',
              backgroundColor: 'transparent'
            }} />)}

                {hasNextPage && !hasError && !inWalletFilterEmptyState && <div ref={sentinelRef} className="h-px" />}
              </div>
            </div>}
        </div>
      </div>
    </div>;
};
export default TableVaultClient;